import { lazy, Suspense, useEffect, useMemo, useState, type ReactNode } from "react";
import { specFromEquipped } from "../char3d/types";
import type { Equipped } from "./Avatar";
import { calm } from "./fx";

/** three.js and the stage are a lazy chunk: the front page first shows the flat avatar and swaps when the stage has arrived */
const Stage = lazy(() => import("../char3d/stage"));

/**
 * The live 3D showpiece of the front page. It only starts when a real graphics card is there (no software rendering), nobody asked for
 * less motion and WebGL works – in every other case `fallback` (the flat avatar in its ring) stays, in a box of the same size so
 * nothing jumps. `onMode` tells the page which one is shown.
 */
export function HeroStage({ equipped, progress, level, xpLeft, fallback, onMode }: { equipped: Equipped; progress: number; level: number; xpLeft: number; fallback: ReactNode; onMode?: (m: "3d" | "flat") => void }) {
  const [mode, setMode] = useState<"checking" | "3d" | "flat">("checking");
  const spec = useMemo(() => specFromEquipped(equipped), [equipped]);
  useEffect(() => {
    let alive = true;
    if (calm()) { setMode("flat"); return; }
    import("../char3d/portrait").then((m) => { if (alive) setMode(m.hardwareGL() ? "3d" : "flat"); }).catch(() => alive && setMode("flat"));
    return () => { alive = false; };
  }, []);
  useEffect(() => { if (mode !== "checking") onMode?.(mode); }, [mode, onMode]);
  const label = xpLeft > 0 ? `Niveau ${level}. ${xpLeft} XP til næste niveau.` : `Niveau ${level}. Klar til næste niveau.`;
  return (
    <div className={`hh-stagebox${mode === "3d" ? " live" : ""}`}>
      {mode === "3d" ? (
        <>
          <Suspense fallback={<div className="hh-stagefb">{fallback}</div>}>
            <Stage spec={spec} progress={progress} ariaLabel={`Din figur på podiet. ${label} Klik på figuren for at få den til at reagere.`} onFail={() => setMode("flat")} />
          </Suspense>
          <span className="hh-lv" aria-hidden="true">Lv.{level}</span>
          <div className="sr-only" role="progressbar" aria-label={`XP til niveau ${level + 1}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)} />
        </>
      ) : (
        <div className="hh-stagefb">{fallback}</div>
      )}
    </div>
  );
}
